import { AudioCue } from '../types/audio';

/**
 * Procedural pink noise generator with resonant low-pass filtering.
 * Generates continuous cosmic background hiss and void static using
 * mathematical 1/f pink noise synthesized directly into an AudioBuffer.
 */
export class NoiseGen {
  private ctx: AudioContext;
  private outputNode: GainNode;
  private filterNode: BiquadFilterNode;
  private noiseGainNode: GainNode;
  private bufferSource: AudioBufferSourceNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private isStarted = false;

  constructor(ctx: AudioContext) {
    this.ctx = ctx;

    // Output node
    this.outputNode = this.ctx.createGain();
    this.outputNode.gain.setValueAtTime(1.0, this.ctx.currentTime);

    // Resonant low-pass filter for cosmic hiss
    this.filterNode = this.ctx.createBiquadFilter();
    this.filterNode.type = 'lowpass';
    this.filterNode.frequency.setValueAtTime(1100, this.ctx.currentTime);
    this.filterNode.Q.setValueAtTime(2.2, this.ctx.currentTime);

    // Internal noise gain
    this.noiseGainNode = this.ctx.createGain();
    this.noiseGainNode.gain.setValueAtTime(0.08, this.ctx.currentTime);

    // Signal routing: BufferSource -> noiseGainNode -> filterNode -> outputNode
    this.noiseGainNode.connect(this.filterNode);
    this.filterNode.connect(this.outputNode);

    // Synthesize procedural pink noise buffer
    this.generatePinkNoiseBuffer();
  }

  public connect(destination: AudioNode): void {
    this.outputNode.connect(destination);
  }

  public getOutputNode(): GainNode {
    return this.outputNode;
  }

  public getFilterNode(): BiquadFilterNode {
    return this.filterNode;
  }

  public getNoiseGainNode(): GainNode {
    return this.noiseGainNode;
  }

  public getNoiseBuffer(): AudioBuffer | null {
    return this.noiseBuffer;
  }

  /**
   * Generates a seamless 4-second procedural stereo pink noise buffer
   * using Paul Kellet's refined 6-pole filter model.
   */
  private generatePinkNoiseBuffer(): void {
    const sampleRate = this.ctx.sampleRate || 44100;
    const durationSeconds = 4.0;
    const length = Math.floor(sampleRate * durationSeconds);
    const channels = 2;

    this.noiseBuffer = this.ctx.createBuffer(channels, length, sampleRate);

    for (let c = 0; c < channels; c++) {
      const data = this.noiseBuffer.getChannelData(c);
      let b0 = 0.0;
      let b1 = 0.0;
      let b2 = 0.0;
      let b3 = 0.0;
      let b4 = 0.0;
      let b5 = 0.0;
      let b6 = 0.0;
      let maxPeak = 0.0;

      for (let i = 0; i < length; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.153852;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.61490 * b5 - white * 0.016898;
        const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
        b6 = white * 0.115926;

        const sample = pink * 0.10;
        data[i] = sample;
        const abs = Math.abs(sample);
        if (abs > maxPeak) maxPeak = abs;
      }

      if (maxPeak > 1.0) {
        for (let i = 0; i < length; i++) {
          data[i] /= maxPeak;
        }
      }
    }
  }

  public start(): void {
    if (this.isStarted || !this.noiseBuffer) return;
    this.isStarted = true;

    this.bufferSource = this.ctx.createBufferSource();
    this.bufferSource.buffer = this.noiseBuffer;
    this.bufferSource.loop = true;
    this.bufferSource.connect(this.noiseGainNode);
    this.bufferSource.start(this.ctx.currentTime);
  }

  public stop(): void {
    if (!this.isStarted) return;
    this.isStarted = false;

    try {
      this.bufferSource?.stop(this.ctx.currentTime);
      this.bufferSource?.disconnect();
    } catch {
      // Ignore if already stopped
    }
    this.bufferSource = null;
  }

  /**
   * Smoothly crossfades noise characteristics across moods.
   */
  public updateCue(cue: AudioCue, duration = 2.0): void {
    if (!this.isStarted) {
      this.start();
    }

    const t = this.ctx.currentTime;
    const endTime = t + duration;

    let targetCutoff = 1100;
    let targetQ = 2.0;
    let targetGain = 0.08 * cue.intensity;

    switch (cue.mood) {
      case 'deep_drone':
        targetCutoff = 800 + cue.intensity * 400;
        targetQ = 1.8;
        targetGain = 0.06 * Math.max(cue.intensity, 0.2);
        break;

      case 'quantum_flutter':
        // Cosmic void hiss with higher frequency presence
        targetCutoff = 1800 + cue.intensity * 800;
        targetQ = 3.8;
        targetGain = 0.14 * (cue.intensity + cue.dissonance * 0.5);
        break;

      case 'subharmonic_pulse':
        // Low muffled background noise
        targetCutoff = 450 + cue.intensity * 200;
        targetQ = 1.2;
        targetGain = 0.04 * cue.intensity;
        break;

      case 'caesura_silence':
        // Dead silence drop
        targetCutoff = 0.001;
        targetQ = 1.0;
        targetGain = 0.0;
        break;

      case 'synaptic_alarm':
        // High harsh static
        targetCutoff = 2800 + cue.intensity * 1200;
        targetQ = 5.0;
        targetGain = 0.18 * (cue.intensity + cue.dissonance);
        break;
    }

    // Filter frequency ramp
    this.filterNode.frequency.cancelScheduledValues(t);
    this.filterNode.frequency.setValueAtTime(this.filterNode.frequency.value, t);
    if (cue.mood === 'caesura_silence') {
      this.filterNode.frequency.linearRampToValueAtTime(0, t + Math.min(0.4, duration));
    } else {
      this.filterNode.frequency.linearRampToValueAtTime(targetCutoff, endTime);
    }

    // Q ramp
    this.filterNode.Q.cancelScheduledValues(t);
    this.filterNode.Q.setValueAtTime(this.filterNode.Q.value, t);
    this.filterNode.Q.linearRampToValueAtTime(targetQ, endTime);

    // Gain ramp
    this.noiseGainNode.gain.cancelScheduledValues(t);
    this.noiseGainNode.gain.setValueAtTime(this.noiseGainNode.gain.value, t);
    this.noiseGainNode.gain.linearRampToValueAtTime(Math.max(0, targetGain), endTime);
  }

  public setFilterCutoff(cutoff: number, duration = 0.1): void {
    const t = this.ctx.currentTime;
    this.filterNode.frequency.cancelScheduledValues(t);
    this.filterNode.frequency.setValueAtTime(this.filterNode.frequency.value, t);
    this.filterNode.frequency.linearRampToValueAtTime(Math.max(0, cutoff), t + duration);
  }

  public setGain(gain: number, duration = 0.1): void {
    const t = this.ctx.currentTime;
    this.noiseGainNode.gain.cancelScheduledValues(t);
    this.noiseGainNode.gain.setValueAtTime(this.noiseGainNode.gain.value, t);
    this.noiseGainNode.gain.linearRampToValueAtTime(Math.max(0, gain), t + duration);
  }
}
